import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Tooltip from '@mui/material/Tooltip';
import HandshakeIcon from '@mui/icons-material/Handshake';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CheckIcon from '@mui/icons-material/Check';
import { logoutUser, switchTenant as switchTenantAction } from '../../../../actions/authentication';
import packageJson from '../../../../../package.json';
import DeviceManager from '../../../Devices/DeviceManager';
import Avatar from '../../Avatar/Avatar';
import { toggleDeviceDialog } from '../../../../actions/devices';
import ChangePasswordDialog from '../../../Admin/ChangePasswordDialog';
import common from '../../../../jsons/common.json';
import axios from 'axios';

export default function Profile() {
    const dispatch = useDispatch();
    const auth = useSelector(state => state.auth);
    const profile = useSelector(state => state.profile);
    const deviceDialogOpen = useSelector(state => state.devices.deviceDialogOpen);
    const newDevices = useSelector(state => state.devices.new);
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const [newDevicesPopup, setNewDevicesPopup] = useState(false);
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);
    const [tenantMenuAnchor, setTenantMenuAnchor] = useState(null);
    const [userTenants, setUserTenants] = useState([]);
    const anchorRef = useRef(null);
    const { t } = useTranslation();
    const open = Boolean(anchorEl);
    const tenantMenuOpen = Boolean(tenantMenuAnchor);
    // Read-only tenant users get a minimal profile menu: log out only.
    const readOnly = auth.user?.readOnly === true;
    const DBAuth = auth.user?.authType?.find(x => x === common.auth.DB);
    const hasPartnerTenants = auth.user?.partnerPortal && userTenants.some(t => t.partner);

    useEffect(() => {
        // Fetch user's tenants for the switcher
        axios.get('/users/mytenants').then(res => {
            if (Array.isArray(res.data)) setUserTenants(res.data);
        }).catch(() => {});
    }, []);

    const handleOpen = (e) => {
        setAnchorEl(e.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const changePassword = () => {
        handleClose();
        setChangePasswordOpen(true);
    };

    const deviceClick = () => {
        handleClose();
        dispatch(toggleDeviceDialog());
    };

    const onLogout = (e) => {
        e.preventDefault();
        dispatch(logoutUser(navigate));
        navigate('/login');
    };

    const profileClick = () => {
        handleClose();
        navigate('/userprofile');
    };

    const partnerPortalClick = async () => {
        handleClose();
        // If already on a partner tenant, just navigate
        if (auth.user?.partner) {
            navigate('/portal/home');
            return;
        }
        // Switch to the first partner tenant — full clean switch
        const partnerTenant = userTenants.find(t => t.partner);
        if (partnerTenant) {
            try {
                await dispatch(switchTenantAction(partnerTenant._id, true));
                return;
            } catch (err) {
                console.error('Tenant switch failed', err);
            }
        }
        navigate('/portal/home');
    };

    const switchTenant = async (tenantId) => {
        setTenantMenuAnchor(null);
        handleClose();
        if (tenantId === auth.user?.tenant) return;
        try {
            await dispatch(switchTenantAction(tenantId));
        } catch (err) {
            console.error('Tenant switch failed', err);
        }
    };

    useEffect(() => {
        let timer;
        if (newDevices.length > 0) {
            setNewDevicesPopup(true);
            timer = setTimeout(() => {
                setNewDevicesPopup(false);
            }, 30000);
        }
        return () => {
            clearTimeout(timer);
        };
    }, [newDevices]);

    return (
        <>
            <IconButton
                onClick={handleOpen}
                id="usericon"
                aria-haspopup="true"
                aria-controls={open ? 'profile-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                color="inherit"
                ref={anchorRef}
            >
                <Avatar user={profile.profile} />
            </IconButton>
            <Menu
                id="profile-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {!readOnly && <MenuItem onClick={profileClick}>{t('profile')}</MenuItem>}
                {!readOnly && <MenuItem onClick={deviceClick}>{t('devices')}</MenuItem>}
                {!readOnly && DBAuth != null &&
                    <MenuItem onClick={changePassword}>{t('changePassword')}</MenuItem>
                }
                {!readOnly && !auth.user?.internal && userTenants.filter(t => !t.partner).length > 1 && (
                    <MenuItem onClick={(e) => { setTenantMenuAnchor(e.currentTarget); }}>
                        <ListItemIcon><SwapHorizIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>{t('switchTenant', 'Switch Tenant')}</ListItemText>
                    </MenuItem>
                )}
                {!readOnly && hasPartnerTenants && (
                    <MenuItem onClick={partnerPortalClick}>
                        <ListItemIcon><HandshakeIcon fontSize="small" /></ListItemIcon>
                        <ListItemText>{t('partnerPortal', 'Partner Portal')}</ListItemText>
                    </MenuItem>
                )}
                {!readOnly && <Divider />}
                <MenuItem onClick={onLogout}>{t('logout')}</MenuItem>
                {!readOnly &&
                    <MenuItem disableRipple sx={{ cursor: 'default', '&:hover': { backgroundColor: 'transparent' } }}>
                        {t('version') + ' ' + packageJson.version + ', ' + auth.user.serverVersion}
                    </MenuItem>
                }
            </Menu>
            <Menu
                anchorEl={tenantMenuAnchor}
                open={tenantMenuOpen}
                onClose={() => setTenantMenuAnchor(null)}
                anchorOrigin={{ horizontal: 'left', vertical: 'top' }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            >
                {userTenants.filter(t => !t.partner).map(tenant => (
                    <MenuItem key={tenant._id} onClick={() => switchTenant(tenant._id)} selected={tenant._id === auth.user?.tenant}>
                        {tenant._id === auth.user?.tenant && (
                            <ListItemIcon><CheckIcon fontSize="small" /></ListItemIcon>
                        )}
                        <ListItemText inset={tenant._id !== auth.user?.tenant}>{tenant.name}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>
            {deviceDialogOpen && <DeviceManager />}
            {newDevices != null && newDevices.length > 0 && (
                <Tooltip open={newDevicesPopup} title={t('newPrinterDetected')} placement="bottom">
                    <div />
                </Tooltip>
            )}
            <ChangePasswordDialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
        </>
    );
}